import { Link, useLoaderData, useParams } from "react-router-dom";
import { Profile } from "../../../services/profile/profileModel";
import SuspenseVideoPlayer from "../../../components/SuspenseVideoPlayer";
import { ReturnButton } from "../../../components/ReturnButton";
import { useTranslation } from "react-i18next";
import SharedInvitationDialog from "./SharedInvitationDialog";
import { authService } from "../../../services/auth/authService";
import { useEffect, useState } from "react";
import SharedCreateAccountDialog from "./SharedCreateAccountDialog";
import ProShell from "../../../components/shell/ProShell";
import { User } from "../../../services/user/userModel";
import {
  isFreeOrganizationAccount,
  Organization,
} from "../../../services/organization/organizationModel";
import { organizationService } from "../../../services/organization/organizationService";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import SubmitButton from "../../../components/SubmitButton";
import { LAST_VIEWED_PROFILE_KEY } from "../OrganizationCandidatesProfilesPage";

const { isLoggedIn: getIsLoggedIn } = authService();

const SharedPage = () => {
  const { t } = useTranslation();
  const { uid } = useParams();
  const { profile, video, shell } = useLoaderData() as {
    profile: Profile;
    video: Promise<Blob> | null;
    shell: { loggedUser: User; organization: Organization } | null;
  };

  const isLoggedIn = getIsLoggedIn();

  const [showCreateAccountCta, setShowCreateAccountCta] = useState(false);

  // Favori et archivage depuis la lecture (Nicolas, 01/11) : mêmes appels que les vignettes de « Mes candidats »,
  // mêmes conditions (pas sur une offre gratuite). État local plutôt qu'un rechargement : relancer le loader
  // compterait une consultation de plus.
  const {
    favoriteSharedProfile,
    unfavoriteSharedProfile,
    archiveSharedProfile,
    unarchiveSharedProfile,
  } = organizationService();
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const shared =
    shell && !isFreeOrganizationAccount(shell.organization, shell.loggedUser)
      ? shell.organization.sharedProfiles.find(
          (p) => p.profile.uid === profile.uid,
        )
      : undefined;
  const [favorited, setFavorited] = useState(!!shared?.favoritedAt);
  const [archived, setArchived] = useState(!!shared?.archivedAt);

  // « Mes candidats » rouvre la page de cette vignette et la montre.
  useEffect(() => {
    if (shell) sessionStorage.setItem(LAST_VIEWED_PROFILE_KEY, profile.uid);
  }, [shell, profile.uid]);

  const page = (
    <div className="layout_content --bg--organization-dashboard">
      <div className="ui-page pro-screen pro-shared">
        {isLoggedIn && (
          <div>
            <ReturnButton forcedUrl="/organization/candidates">
              {t("common:header.MY_CANDIDATES")}
            </ReturnButton>
          </div>
        )}
        <div className="ui-ph">
          <div className="ui-ph_t">
            <h1>
              {t("organization:shared-profile.PROFILE_OF", {
                name: profile.user!.firstname + " " + profile.user?.lastname,
              })}
            </h1>
          </div>
          <div className="pro-row pro-shared_actions">
            {shared && !archived && (
              <SubmitButton
                className="btn--white --btn--s"
                aria-pressed={favorited}
                onClick={() =>
                  (favorited ? unfavoriteSharedProfile : favoriteSharedProfile)(
                    profile.uid,
                    shell!.organization.id,
                  ).then(() => setFavorited(!favorited))
                }
              >
                <span
                  className={favorited ? "pro-ico --star-on" : "pro-ico --star"}
                  aria-hidden="true"
                />
                {favorited
                  ? t("candidate:tooltips.UNFAVORITE")
                  : t("candidate:tooltips.FAVORITE")}
              </SubmitButton>
            )}
            {shared && (
              <SubmitButton
                className="btn--white --btn--s"
                onClick={() =>
                  archived
                    ? unarchiveSharedProfile(
                        profile.uid,
                        shell!.organization.id,
                      ).then(() => setArchived(false))
                    : confirm(
                        "organization:shared-profile.archive.CONFIRM",
                        () =>
                          archiveSharedProfile(
                            profile.uid,
                            shell!.organization.id,
                          ).then(() => setArchived(true)),
                        true,
                      )
                }
              >
                <span
                  className={
                    archived ? "pro-ico --unarchive" : "pro-ico --archive"
                  }
                  aria-hidden="true"
                />
                {archived
                  ? t("candidate:tooltips.UNARCHIVE")
                  : t("candidate:tooltips.ARCHIVE")}
              </SubmitButton>
            )}
            <Link
              className="link --danger --underlined"
              to={`${isLoggedIn ? "/organization/contact" : "/contact"}?object=${encodeURIComponent(
                t("organization:shared-profile.REPORT_PROFILE_OBJECT", {
                  profileId: profile.id,
                  name: profile.user?.firstname + " " + profile.user?.lastname,
                  userId: profile.user?.id,
                }),
              )}&message=${encodeURIComponent(t("organization:shared-profile.REASON"))}`}
            >
              {t("organization:shared-profile.REPORT_PROFILE")}
            </Link>
          </div>
        </div>
        <div className="pro-split --single">
          <div
            style={{
              overflow: "hidden",
            }}
          >
            <div
              className={"video-container --placeholder"}
              style={
                !video
                  ? {
                      backgroundImage: `url("${profile.thumbnail}")`,
                      backgroundPosition: "center",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      filter: "blur(15px)",
                    }
                  : {}
              }
            >
              {video && (
                <SuspenseVideoPlayer
                  videoPromise={video}
                  thumbnail={profile.thumbnail}
                  onPause={() => {
                    if (!isLoggedIn) setShowCreateAccountCta(true);
                  }}
                  onEnded={() => {
                    if (!isLoggedIn) setShowCreateAccountCta(true);
                  }}
                />
              )}
            </div>
          </div>
        </div>
        {/* Bloc CTA "Attirer plus de candidats ?" désactivé : le lien vers
            /organization/capsules était jugé déceptif et peu clair.
            À réactiver (ou remplacer) quand une meilleure formulation sera trouvée.
        {isLoggedIn && (
          <div className="cblock card --outlined-on-bg">
            <div className="card_body">
              <div className="--txt--center">
                {t("organization:shared-profile.2MM_DESCRIPTION")}
              </div>
              <div className="lblock --txt--center">
                <Link
                  className="btn--white"
                  to="/organization/capsules"
                  dangerouslySetInnerHTML={{
                    __html: t("organization:shared-profile.CONNECTED_CTA"),
                  }}
                />
              </div>
            </div>
          </div>
        )}
        */}
      </div>
      {!video && !isLoggedIn && (
        <SharedInvitationDialog uid={uid!} profile={profile} />
      )}
      {showCreateAccountCta && (
        <SharedCreateAccountDialog
          uid={uid!}
          onClose={() => setShowCreateAccountCta(false)}
        />
      )}
    </div>
  );

  return shell ? (
    <ProShell loggedUser={shell.loggedUser} organization={shell.organization}>
      {page}
    </ProShell>
  ) : (
    page
  );
};

export default SharedPage;
